/* =========================================================================
   auth-gate.tsx — Enterprise auth gate with magic link, OAuth, password,
   and passcode fallback. Uses AuthContext for state.
   ========================================================================= */

import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./auth-context";
import { EstateAccessGate } from "./estate-access-gate";
import { estateSignInUrl } from "./estate-sso";
import { isDoltBackend } from "./dolt-mode";
import { checkRateLimit } from "./ws-security/rate-limiter";
import { getWorkspaceNotice } from "./security-disclosure";
import { toastGlobal } from "./toast";
import { useFocusTrap, useReducedMotion } from "./hooks";
import { Wordmark } from "./wordmark";
import { TenureLoading } from "./tenure-loading";

// --- Icons (inline SVG for zero dependency) ---
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M12 1C5.92 1 1 5.92 1 12c0 4.86 3.15 8.99 7.52 10.44.55.1.75-.24.75-.53v-1.87c-3.06.67-3.7-1.47-3.7-1.47-.5-1.27-1.22-1.61-1.22-1.61-1-.68.08-.67.08-.67 1.1.08 1.68 1.13 1.68 1.13.98 1.68 2.57 1.2 3.2.91.1-.71.38-1.2.7-1.47-2.44-.28-5.01-1.22-5.01-5.44 0-1.2.43-2.19 1.13-2.96-.11-.28-.49-1.4.11-2.92 0 0 .92-.3 3.02 1.13a10.5 10.5 0 0 1 5.52 0c2.1-1.43 3.01-1.13 3.01-1.13.6 1.52.22 2.64.11 2.92.71.77 1.13 1.75 1.13 2.96 0 4.22-2.57 5.16-5.02 5.44.39.34.74 1 .74 2.02v3c0 .29.2.64.76.53C19.86 20.98 23 16.85 23 12c0-6.08-4.92-11-11-11z" />
  </svg>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// --- Auth skeleton loader ---
function AuthSkeleton() {
  return <TenureLoading />;
}

// --- Auth gate UI ---
export function AuthGate({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const auth = useAuth();
  const reducedMotion = useReducedMotion();
  const trapRef = useFocusTrap(!auth.authed && !auth.loading);
  // Invite-only workspace: no self-registration (signups are disabled server-side
  // too — accounts are provisioned via the Supabase admin API).
  const [mode, setMode] = useState<"login" | "magic">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [ackBeta, setAckBeta] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const triggerShake = useCallback((msg: string) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }, []);

  // Every sign-in path (OAuth, magic link, password, passcode) must pass
  // through this acknowledgement — the gate is the only place the disclaimer is shown.
  const requireAck = useCallback((): boolean => {
    if (ackBeta) return true;
    triggerShake("Please tick the beta acknowledgement to continue.");
    return false;
  }, [ackBeta]); // eslint-disable-line react-hooks/exhaustive-deps -- triggerShake is stable

  const handleOAuth = useCallback(
    async (provider: "google" | "github") => {
      if (!requireAck()) return;
      setBusy(true);
      setError("");
      const { error: err } = await auth.signInWithOAuth(provider);
      if (err) triggerShake(err.message);
      setBusy(false);
    },
    [requireAck, auth, triggerShake],
  );

  const handleMagicLink = useCallback(async () => {
    setError("");
    setInfo("");
    if (!email.trim()) {
      triggerShake("Email is required");
      return;
    }
    setBusy(true);
    const { error: err } = await auth.signInWithMagicLink(email.trim());
    setBusy(false);
    if (err) {
      triggerShake(err.message);
    } else {
      setMagicSent(true);
      setInfo("Magic link sent! Check your email inbox.");
      toastGlobal("Magic link sent to your email", "success", 4000);
    }
  }, [email, auth, triggerShake]);

  const handlePassword = useCallback(async () => {
    setError("");
    setInfo("");
    if (!email.trim() || !password.trim()) {
      triggerShake("Email and password are required");
      return;
    }
    const limit = checkRateLimit("password-attempt", 3, 300000); // 3 attempts / 5 min
    if (!limit.allowed) {
      triggerShake(
        "Too many password attempts. Please try again in " +
          limit.retryAfter +
          "s.",
      );
      return;
    }
    setBusy(true);
    const { error: err } = await auth.signInWithPassword(
      email.trim(),
      password,
    );
    setBusy(false);
    if (err) {
      triggerShake(err.message);
    }
  }, [email, password, auth, triggerShake]);

  const handlePasscode = useCallback(() => {
    setError("");
    setInfo("");
    if (auth.verifyPasscode(code.trim())) {
      toastGlobal("Access granted", "success", 2000);
    } else {
      triggerShake("Incorrect passcode");
    }
  }, [code, auth, triggerShake]);

  const submit = useCallback(() => {
    if (!requireAck()) return;
    if (auth.usePasscodeFallback) {
      const limit = checkRateLimit("auth-attempt", 5, 60000);
      if (!limit.allowed) {
        triggerShake(
          "Too many attempts. Please try again in " + limit.retryAfter + "s.",
        );
        return;
      }
      handlePasscode();
      return;
    }
    if (mode === "magic") {
      handleMagicLink();
    } else {
      handlePassword();
    }
  }, [
    requireAck,
    auth.usePasscodeFallback,
    mode,
    handlePasscode,
    handleMagicLink,
    handlePassword,
    triggerShake,
  ]);

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") submit();
    },
    [submit],
  );

  if (auth.authed) return <EstateAccessGate>{children}</EstateAccessGate>;
  if (auth.loading) return <AuthSkeleton />;

  if (auth.productionAuthUnavailable) {
    return (
      <main className="auth-page" aria-labelledby="auth-unavailable-title">
        <section className="auth-card" role="alert">
          <div className="auth-logo">
            <Wordmark size="lg" />
          </div>
          <h1 id="auth-unavailable-title">Service temporarily unavailable</h1>
          <p>
            Secure sign-in is not configured for this deployment. No scenario
            data has been opened.
          </p>
          <p className="p-small">Please contact the workspace administrator.</p>
        </section>
      </main>
    );
  }

  const usePasscode = auth.usePasscodeFallback;

  return (
    <div
      ref={trapRef}
      className="signin-shell"
      role="main"
      aria-label="Sign in to TENURE"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flexDirection: "column",
        overflowY: "auto",
        padding: "24px 16px",
        background: "hsl(var(--c-background))",
        fontFamily: 'var(--font-display, "Inter", system-ui, sans-serif)',
        zIndex: 9999,
      }}
    >
      {/* Ambient glow */}
      <div
        style={{
          position: "absolute",
          top: "30%",
          left: "50%",
          width: "min(400px, 100%)",
          height: 400,
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, hsl(var(--c-primary) / 0.12) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <div
        className="signin-card"
        style={{
          position: "relative",
          background: "hsl(var(--c-card) / 0.9)",
          border: "1px solid hsl(var(--c-border))",
          borderRadius: 16,
          boxShadow: "var(--shadow-xl)",
          backdropFilter: "blur(24px)",
          animation:
            shake && !reducedMotion ? "authShake 0.4s ease-in-out" : undefined,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 20,
          }}
        >
          <Wordmark size="lg" tagline />
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
          {usePasscode ? "Enter access code" : "Sign in to TENURE"}
        </div>
        <div
          className="p-small"
          style={{ color: "hsl(var(--c-foreground-muted))", marginBottom: 24 }}
        >
          {usePasscode
            ? "This workspace is invite-only."
            : isDoltBackend()
              ? "Access your private scenarios and saved revisions."
              : "Access your saved scenarios and collaborate."}
        </div>

        {usePasscode ? (
          <input
            ref={inputRef}
            type="password"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError("");
              setInfo("");
            }}
            onKeyDown={onKey}
            placeholder="Access code"
            aria-label="Access code"
            autoComplete="off"
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: 15,
              borderRadius: 10,
              border: `1px solid ${error ? "hsl(var(--c-destructive))" : "hsl(var(--c-border))"}`,
              background: "hsl(var(--c-background))",
              color: "hsl(var(--c-foreground))",
              marginBottom: 16,
            }}
          />
        ) : (
          <>
            {/* Estate single sign-on — the Website front door hands the
                session back to this app via /auth/callback. */}
            <a
              href={estateSignInUrl()}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 12px",
                marginBottom: 12,
                fontSize: 14,
                fontWeight: 500,
                borderRadius: 10,
                border: "1px solid hsl(var(--c-border))",
                background: "hsl(var(--c-primary) / 0.12)",
                color: "hsl(var(--c-foreground))",
                textDecoration: "none",
              }}
            >
              Sign in with Stratosfyre
            </a>

            {/* OAuth buttons */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => handleOAuth("google")}
                disabled={busy}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "10px 12px",
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: 10,
                  border: "1px solid hsl(var(--c-border))",
                  background: "hsl(var(--c-card-elevated))",
                  color: "hsl(var(--c-foreground))",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <GoogleIcon /> Google
              </button>
              <button
                type="button"
                onClick={() => handleOAuth("github")}
                disabled={busy}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "10px 12px",
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: 10,
                  border: "1px solid hsl(var(--c-border))",
                  background: "hsl(var(--c-card-elevated))",
                  color: "hsl(var(--c-foreground))",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <GitHubIcon /> GitHub
              </button>
            </div>

            {/* Divider */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: "hsl(var(--c-border))",
                }}
              />
              <span
                className="p-micro"
                style={{ color: "hsl(var(--c-foreground-muted))" }}
              >
                or
              </span>
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: "hsl(var(--c-border))",
                }}
              />
            </div>

            {/* Mode tabs */}
            <div
              style={{
                display: "flex",
                gap: 4,
                marginBottom: 16,
                background: "hsl(var(--c-card-elevated))",
                borderRadius: 10,
                padding: 4,
              }}
            >
              {(["login", "magic"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMode(m);
                    setError("");
                    setInfo("");
                    setMagicSent(false);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: "none",
                    background:
                      mode === m ? "hsl(var(--c-primary))" : "transparent",
                    color:
                      mode === m
                        ? "hsl(var(--c-primary-fg))"
                        : "hsl(var(--c-foreground-muted))",
                    cursor: "pointer",
                  }}
                >
                  {m === "login" ? "Email sign-in" : "Magic Link"}
                </button>
              ))}
            </div>

            <input
              ref={inputRef}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
                setInfo("");
              }}
              onKeyDown={onKey}
              placeholder="Email"
              aria-label="Email"
              autoComplete="email"
              style={{
                width: "100%",
                padding: "12px 14px",
                fontSize: 15,
                borderRadius: 10,
                border: `1px solid ${error ? "hsl(var(--c-destructive))" : "hsl(var(--c-border))"}`,
                background: "hsl(var(--c-background))",
                color: "hsl(var(--c-foreground))",
                marginBottom: 12,
              }}
            />

            {mode !== "magic" && (
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                  setInfo("");
                }}
                onKeyDown={onKey}
                placeholder="Password"
                aria-label="Password"
                autoComplete="current-password"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: 15,
                  borderRadius: 10,
                  border: `1px solid ${error ? "hsl(var(--c-destructive))" : "hsl(var(--c-border))"}`,
                  background: "hsl(var(--c-background))",
                  color: "hsl(var(--c-foreground))",
                  marginBottom: 16,
                }}
              />
            )}
          </>
        )}

        {/* Beta / no-advice acknowledgement — gates every sign-in path */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginBottom: 16,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={ackBeta}
            onChange={(e) => {
              setAckBeta(e.target.checked);
              setError("");
              if (e.target.checked) {
                try {
                  localStorage.setItem(
                    "__sf_beta_ack",
                    new Date().toISOString(),
                  );
                } catch {
                  /* private browsing — acknowledgement still gates this session */
                }
              }
            }}
            style={{
              marginTop: 2,
              flexShrink: 0,
              accentColor: "hsl(var(--c-primary))",
            }}
          />
          <span
            className="p-micro"
            style={{
              color: "hsl(var(--c-foreground-muted))",
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            TENURE is in beta. Its outputs do not constitute tax, accounting, or
            financial advice, and I will not rely on them for decision-making
            without first verifying their correctness.
          </span>
        </label>

        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="p-small"
            style={{ color: "hsl(var(--c-destructive))", marginBottom: 16 }}
          >
            {error}
          </div>
        )}
        {info && (
          <div
            role="status"
            aria-live="polite"
            className="p-small"
            style={{ color: "hsl(var(--c-success))", marginBottom: 16 }}
          >
            {info}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={busy || (mode === "magic" && magicSent)}
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 10,
            border: "none",
            background: "hsl(var(--c-primary))",
            color: "hsl(var(--c-primary-fg))",
            cursor:
              busy || (mode === "magic" && magicSent)
                ? "not-allowed"
                : "pointer",
            opacity: busy || (mode === "magic" && magicSent) ? 0.7 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {busy ? (
            <>
              <span
                className="spinner"
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid hsl(var(--c-primary-fg) / 0.3)",
                  borderTopColor: "hsl(var(--c-primary-fg))",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  display: "inline-block",
                }}
              />
              Working…
            </>
          ) : usePasscode ? (
            "Unlock"
          ) : mode === "magic" ? (
            <>
              <MailIcon /> {magicSent ? "Link sent" : "Send magic link"}
            </>
          ) : (
            <>
              <LockIcon /> Sign in
            </>
          )}
        </button>
        {!usePasscode && (
          <div
            role="note"
            className="p-micro"
            style={{
              marginTop: "16px",
              padding: "8px 12px",
              background: "hsl(var(--c-card-elevated))",
              borderLeft: "3px solid hsl(var(--c-primary) / 0.6)",
              borderRadius: "4px",
              color: "hsl(var(--c-foreground-muted))",
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            {getWorkspaceNotice()}
          </div>
        )}
      </div>
    </div>
  );
}
